import { defineStore } from 'pinia';
import type { Company, CompanyId } from "@/types/Company.ts";
import axios from 'axios';

export interface PatientPreferences {
  search: string|null;
  companies: CompanyId[];
};

export interface MonitoringPreferences {
  search: string|null;
  companies: CompanyId[];
  importance?: boolean;
};

export interface TagPreferences {
  search: string|null;
  companies: CompanyId[];
};

export interface CompanyPreferences {
  search: string|null;
  companies: CompanyId[];
};

export interface ReportingPreferences {
  search: string|null;
  companies: CompanyId[];
};

interface PreferencesState {
      monitoring: {
        search: string;
        companies: CompanyId[],
        importance: boolean;
        tableView: boolean;
        status:null,
      },
      tags: {
        search: string;
        tableView: boolean;
        status: null,
        companies: CompanyId[],
      },
      company: {
        search: string;
        tableView: boolean;
        status: null,
      },
      appusers: {
        search: string;
        tableView: boolean;
        status: null,
        companies: CompanyId[]|null,
      },
      companies: CompanyId[]|null,
      barcoding: {
        default: string|null,
        preference: string|null
      },
      timezone: {
        useBrowserTime: boolean;
      },
      patients: {
        tableView: boolean;
        search: string;
        status:null,
        companies: CompanyId[],
      },
      reporting: {
        tableView: boolean;
        search: string;
        status:null,
        companies: CompanyId[],
      },
      absoluteScheduleTableView: boolean;
}

function defaultState() {
  return {
    monitoring: {
      search: '',
      companies: [],
      importance: false,
      tableView: false,
      status:null,
    },
    tags: {
      search: '',
      tableView: true,
      status: null,
      companies: [],
    },
    company: {
      search: '',
      tableView: true,
      status: null,
    },
    appusers: {
      search: '',
      tableView: true,
      status: null,
      companies: [],
    },
    companies: null,
    barcoding: {
      default: null,
      preference: null
    },
    timezone: {
      useBrowserTime: false
    },
    patients: {
      tableView: false,
      search: '',
      status:null,
      companies: [],
    },
    reporting: {
      tableView: false,
      search: '',
      status:null,
      companies: [],
    },
    absoluteScheduleTableView: false,
  };
}

export type PreferenceKey = 'monitoring' | 'users' | 'patients' | 'tags' | 'companiesX' | 'barcoding' | 'timezone';

export const usePreferencesStore = defineStore('preferences', {
  persist: true,
  state: (): PreferencesState => (defaultState()),
  actions: {
    updateDefaultCamera(payload: string|null) {
      this.barcoding.default = payload;
    },
    updatePreference(payload: string): void {
      this.barcoding.preference = payload;
    },
    updateTimezone(payload: boolean): void {
      if (this.timezone == undefined) {
        this.timezone = {
          useBrowserTime: false
        };
      }
      this.timezone.useBrowserTime = payload;
      if (payload === false) {
        delete axios.defaults.headers.common['X-TIMEZONE-OFFSET-MINUTES'];
        return;
      }
      const date = new Date();
      axios.defaults.headers.common['X-TIMEZONE-OFFSET-MINUTES'] = date.getTimezoneOffset();
    },
    resetPreferences(): void {
      Object.assign(this, defaultState());
    },
  },
});

export type PreferencesStore = ReturnType<typeof usePreferencesStore>;
export {};
